import {
  BookingErrorRecoveryQueryClient,
  refreshBookingAfterOperationError,
} from './operationErrorRecovery';

export interface ActiveOperationRecheckBooking {
  id: string;
  activeOperationStatus?: 'in_progress' | 'ambiguous' | 'reconciling' | 'stalled';
  activeOperationRecheckAt?: string;
}

export function earliestActiveOperationRecheckBooking<
  TBooking extends ActiveOperationRecheckBooking,
>(bookings: TBooking[] | undefined): TBooking | null {
  let earliest: { booking: TBooking; deadline: number } | null = null;
  for (const booking of bookings ?? []) {
    const deadline = activeOperationRecheckDeadline(booking);
    if (deadline == null) continue;
    if (!earliest || deadline < earliest.deadline) {
      earliest = { booking, deadline };
    }
  }
  return earliest?.booking ?? null;
}

export function scheduleActiveOperationRecheck<TBooking>({
  booking,
  fetchBooking,
  onBooking,
  queryClient,
  now = Date.now,
}: {
  booking: ActiveOperationRecheckBooking | null | undefined;
  fetchBooking: (bookingId: string) => Promise<TBooking>;
  onBooking?: (booking: TBooking) => void;
  queryClient: BookingErrorRecoveryQueryClient;
  now?: () => number;
}): () => void {
  const deadline = activeOperationRecheckDeadline(booking);
  if (deadline == null || !booking) return () => undefined;

  let active = true;
  const timeout = setTimeout(() => {
    void refreshBookingAfterOperationError({
      bookingId: booking.id,
      fetchBooking,
      onBooking: (updated) => {
        if (active) onBooking?.(updated);
      },
      queryClient,
    });
  }, Math.max(deadline - now(), 0));

  return () => {
    active = false;
    clearTimeout(timeout);
  };
}

function activeOperationRecheckDeadline(
  booking: ActiveOperationRecheckBooking | null | undefined,
): number | null {
  if (booking?.activeOperationStatus !== 'in_progress') return null;
  if (!booking.activeOperationRecheckAt) return null;
  const deadline = Date.parse(booking.activeOperationRecheckAt);
  return Number.isFinite(deadline) ? deadline : null;
}
